import * as React from 'react';
import { Flex, Box, FormControl, FormLabel, FormHelperText, Input, Tooltip } from '@chakra-ui/react';

import { ApproveDataIcon } from '../../iconButtons/ApproveDataIcon';
import { EditDataIcon } from '../../iconButtons/EditDataIcon';
import { getFormatDateFromObject, getDateFromString } from '../../../utilities/helpers';
import { IDate } from '../../../types';

function withConfirm(Component: any, componentType?: string) {
  interface ITheme {
    detailsBorderColor?: string;
    detailsColor?: string;
    detailsFontSize?: number;
    otherValue?: string;
    iconBorderColor?: string;
    iconBorderColorInactive?: string;
    fontFamily?: string;
  }

  interface IOption {
    label: string;
    value: string;
  }

  interface IProps {
    theme?: ITheme;
    showDescription?: boolean;
    inputHelperText?: string;
    forwardedRef?: ((instance: HTMLInputElement | null) => void) | React.RefObject<HTMLInputElement> | null | undefined;
    onBlur?: (event: any) => void;
    onFocus?: (event: any) => void;
    otherValue?: string | number;
    otherOptions?: IOption[];
    otherOptionLabel?: string;
    disableOther?: boolean;
    onChangeOtherText?: (event: React.FormEvent<HTMLInputElement>) => void;
    id?: string;
    label?: string;
    subText?: string;
    autoFocus?: boolean;
    canUndo?: boolean;
    onUndo?: () => void;
    importEnabled?: boolean;
    importUpdateEnabled?: boolean;
    value?: string | IDate;
    importValue?: string;
    importApproved?: boolean;
    importEdited?: boolean;
    onImportApprove?: () => void;
    onImportEdit?: () => void;
    onImportUndo?: () => void;
    hideEditButton?: boolean;
    mb?: number;
  }

  interface IState {
    showDescription: boolean;
  }

  class ConfirmWrapper extends React.Component<IProps, IState> {
    constructor(props: IProps) {
      super(props);
      this.state = {
        showDescription: !!props.showDescription
      };
    }

    onFocus = (evt: any) => {
      this.props.onFocus && this.props.onFocus(evt);
    };
    onBlur = (evt: any) => {
      this.props.onBlur && this.props.onBlur(evt);
    };

    _renderImportHelper = () => {
      return (
        <>
          {!this.props.importUpdateEnabled && (
            <ApproveDataIcon
              id={`${this.props.id}-import-approve`}
              type={this.props.importApproved ? 'clicked' : 'unclicked'}
              onClick={this.props.importApproved ? undefined : this.props.onImportApprove}
            />
          )}

          {!this.props.hideEditButton && (
            <EditDataIcon
              id={`${this.props.id}-import-edit`}
              type={this.props.importApproved ? 'focus-off' : 'unclicked'}
              onClick={this.props.onImportEdit}
            />
          )}
        </>
      );
    };

    onUndo = (evt: React.MouseEvent<HTMLElement>) => {
      evt.preventDefault && evt.preventDefault();
      return this.props.importEnabled ? this.props.onImportUndo() : this.props.onUndo();
    };

    render() {
      const dataId = this.props.id || 'field';
      let valueStr: string | undefined;
      let importValue = this.props.importValue;
      // If object was passed to value, it's a date.
      // Convert it to display string (when importUpdateEnabled === true, it will be displayed in import field)
      if (componentType === 'date-input') {
        const dateAsObject = getDateFromString(importValue);
        importValue = getFormatDateFromObject(dateAsObject);
      }
      if (this.props.value instanceof Object) {
        valueStr = getFormatDateFromObject(this.props.value);
      } else {
        valueStr = this.props.value;
      }

      return (
        <Box data-selector={`${dataId}-confirm-wrapper`} mb={this.props.mb ?? 10}>
          <FormControl data-selector={`${dataId}-import-field`}>
            {this.props.importEnabled && !this.props.importEdited ? (
              <>
                {this.props.label ? (
                  <FormLabel as="legend" data-selector={`${dataId}-import-label`} fontSize="lg">
                    {this.props.label}
                  </FormLabel>
                ) : null}

                {this.props.subText ? <FormHelperText mb={3}>{this.props.subText}</FormHelperText> : null}

                <Flex gap={{ base: 1, lg: 2 }}>
                  <Tooltip hasArrow placement="bottom" label="Please use the buttons to the right to confirm or edit">
                    <Box
                      id={`${dataId}-import-input-field`}
                      data-selector={`${dataId}-import-input-field`}
                      borderRadius="lg"
                      flexGrow="1"
                      px="1.125rem"
                      py={3}
                      fontSize="lg"
                      lineHeight={6}
                      bgColor="gray.100"
                      _dark={{ bgColor: 'gray.800' }}
                    >
                      {!this.props.importUpdateEnabled ? importValue : valueStr}
                    </Box>
                  </Tooltip>

                  {this.props.importEnabled ? this._renderImportHelper() : null}
                </Flex>
              </>
            ) : (
              <>
                <Component
                  {...this.props}
                  onFocus={this.onFocus}
                  onBlur={this.onBlur}
                  canUndo={this.props.importEnabled ? this.props.importEdited : this.props.canUndo}
                  onUndo={this.onUndo}
                  autoFocus={this.props.importEnabled && this.props.importEdited ? true : this.props.autoFocus}
                />
              </>
            )}
          </FormControl>

          {(this.props.importEnabled &&
            !this.props.importEdited &&
            this.props.otherOptions &&
            this.props.otherOptions.length &&
            this.props.otherOptions.findIndex(option => option.value === this.props.value) > -1) ||
          this.props.value === 'other' ? (
            <>
              {this.props.otherOptionLabel ? (
                <FormLabel data-selector={`${dataId}-import-label`}>{this.props.otherOptionLabel}</FormLabel>
              ) : null}

              <Input
                type="text"
                size="lg"
                data-selector={`${this.props.id}_other`}
                isDisabled={this.props.disableOther}
                onChange={this.props.onChangeOtherText}
                onBlur={this.props.onBlur}
                onFocus={this.props.onFocus}
                value={this.props.otherValue}
              />
            </>
          ) : null}
        </Box>
      );
    }
  }

  function forwardRef(props: any, ref: any) {
    return <ConfirmWrapper {...props} forwardedRef={ref} />;
  }

  // Give this component a more helpful display name in DevTools.
  // e.g. 'ForwardRef(withConfirm(MyComponent))'
  const name = Component.displayName || Component.name;
  forwardRef.displayName = `withConfirm(${name})`;

  return React.forwardRef(forwardRef);
}

export { withConfirm };
